import * as React from "react"

const MOBILE_BREAKPOINT = 640

/**
 * SSR-safe hook to detect mobile viewport
 *
 * HYDRATION FIX: Returns `false` during SSR and initial hydration,
 * then updates to actual value after mount. This prevents hydration
 * mismatch by ensuring server and client render identically on first pass.
 *
 * @returns boolean - true if viewport is mobile-sized
 */
export function useIsMobile(): boolean {
  // Start with false to match SSR output (no window available on server)
  // This ensures hydration consistency: server=false, initial client=false
  const [isMobile, setIsMobile] = React.useState(false)
  const [hasMounted, setHasMounted] = React.useState(false)

  React.useEffect(() => {
    // Mark as mounted - now safe to read window
    setHasMounted(true)

    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    // Set actual value after mount
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)

    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  // During SSR or before mount, return false (consistent with server)
  // After mount, return actual value
  return hasMounted ? isMobile : false
}
